<template>
    <div>
        <h1 class="text-h3">{{ athlete.name }}</h1>
        <div class="my-4">
            <v-chip
                v-if="athlete.is_sr_national_team_tra"
                dark
                color="blue"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                SR National Team TRA
            </v-chip>
            <v-chip
                v-if="athlete.is_sr_national_team_dmt"
                dark
                color="green"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                SR National Team DMT
            </v-chip>
            <v-chip
                v-if="athlete.is_sr_national_team_tum"
                dark
                color="red"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                SR National Team TUM
            </v-chip>

            <v-chip
                v-if="athlete.is_jr_national_team_tra"
                dark
                color="blue"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                JR National Team TRA
            </v-chip>
            <v-chip
                v-if="athlete.is_jr_national_team_dmt"
                dark
                color="green"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                JR National Team DMT
            </v-chip>
            <v-chip
                v-if="athlete.is_jr_national_team_tum"
                dark
                color="red"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                JR National Team TUM
            </v-chip>

            <v-chip
                v-if="athlete.is_edp_team_tra"
                dark
                color="blue"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                EDP Team TRA
            </v-chip>
            <v-chip
                v-if="athlete.is_edp_team_dmt"
                dark
                color="green"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                EDP Team DMT
            </v-chip>
            <v-chip
                v-if="athlete.is_edp_team_tum"
                dark
                color="red"
                class="font-weight-bold mx-1"
                style="margin-top: -0.5rem"
            >
                EDP Team TUM
            </v-chip>
        </div>
        <div v-if="canEdit" class="my-4">
            <v-btn @click.prevent="athleteProfileModal = true">
                <v-icon class="mr-2">mdi-pen</v-icon>
                Edit Profile
            </v-btn>
        </div>
        <h2 class="text-h5">
            <span v-if="athlete.gender.toLowerCase() === 'f'">Female</span>
            <span v-else>Male</span>
            ·
            {{ athlete.age }} yrs
        </h2>
        <p class="text-90">
            <inertia-link :href="`/clubs/${athlete.club_id}`">
                {{ athlete.club.name }}
            </inertia-link>
        </p>

        <p v-if="athlete.has_bio">
            <span v-if="athlete.current_residence">Resides in {{ athlete.current_residence }}</span>
            <template v-if="athlete.hometown && athlete.hometown !== athlete.current_residence">
                <br />
                <span>Hometown: {{ athlete.hometown }}</span>
            </template>
        </p>

        <div
            v-if="athlete.has_social"
            class="mb-4 d-flex flex-row"
            :class="{ 'justify-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }"
            style="column-gap: 0.5rem"
        >
            <div>
                <a v-if="athlete.instagram" :href="`https://instagram.com/${athlete.instagram}`" target="_blank">
                    <instagram-icon size="25" :fill="$vuetify.theme.dark ? 'white' : 'black'" />
                </a>
            </div>
            <div>
                <a v-if="athlete.facebook" :href="`https://facebook.com/${athlete.facebook}`" target="_blank">
                    <facebook-icon size="25" :fill="$vuetify.theme.dark ? 'white' : 'black'" />
                </a>
            </div>
            <div>
                <a v-if="athlete.tiktok" :href="`https://tiktok.com/@${athlete.tiktok}`" target="_blank">
                    <tik-tok-icon size="25" :fill="$vuetify.theme.dark ? 'white' : 'black'" />
                </a>
            </div>
            <div>
                <a v-if="athlete.twitter" :href="`https://twitter.com/${athlete.twitter}`" target="_blank">
                    <twitter-icon size="25" :fill="$vuetify.theme.dark ? 'white' : 'black'" />
                </a>
            </div>
        </div>

        <p>
            <s-follow :entity="athlete" />
            <v-btn
                v-if="!canEdit && $page.props.auth.user"
                rounded
                small
                outlined
                class="ml-4"
                @click="claimProfileModal = true"
            >
                <v-icon small class="mr-1">mdi-account-plus-outline</v-icon>
                <span>Claim Profile</span>
            </v-btn>

            <v-btn
                v-if="canEdit"
                rounded
                small
                outlined
                color="success"
                class="ml-2"
                @click.prevent="$emit('switch-tab', 'tab-goals')"
            >
                <v-icon small class="mr-1">mdi-trophy</v-icon>
                Make a Goal
            </v-btn>
        </p>

        <p v-if="athlete.bio" v-html="athlete.bio.replace(/\n/g, '<br />')"></p>

        <v-dialog v-model="claimProfileModal" transition="dialog-top-transition" width="500">
            <claim-profile-modal :athlete="athlete" @close="claimProfileModal = false" />
        </v-dialog>

        <v-dialog v-model="athleteProfileModal" transition="dialog-top-transition" width="500">
            <athlete-profile-modal :athlete="athlete" @close="athleteProfileModal = false" />
        </v-dialog>
    </div>
</template>

<script>
import { FacebookIcon, InstagramIcon, TikTokIcon, TwitterIcon } from 'vue-simple-icons';
import Athlete from '@/Models/Athlete.js';
import SFollow from '@/Components/SFollow.vue';
import ClaimProfileModal from '@/Components/Modals/ClaimProfileModal.vue';
import User from '@/Models/User.js';
import AthleteProfileModal from '@/Components/Modals/AthleteProfileModal.vue';

export default {
    name: 'AthleteHeader',

    components: {
        AthleteProfileModal,
        ClaimProfileModal,
        SFollow,
        FacebookIcon,
        TwitterIcon,
        TikTokIcon,
        InstagramIcon,
    },

    props: {
        athleteId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        claimProfileModal: false,
        athleteProfileModal: false,
    }),

    computed: {
        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations.affiliate').find(this.$page.props.auth.user.id);
        },

        athlete() {
            return Athlete.query().with(['club']).find(this.athleteId);
        },

        canEdit() {
            if (!this.athlete) {
                return false;
            }

            return this.user?.canEdit(this.athlete);
        },
    },
};
</script>

<style scoped lang="scss"></style>
